import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_STORY_MAX_VIDEOS,
  DEFAULT_STORY_VIDEO_MAX_BYTES,
  type PublicStoryMedia,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  completeStoryPhoto,
  initiateStoryPhoto,
  listPublicStoryMedia,
  removeStoryPhoto,
  signStoryMediaParts,
  uploadStoryPart,
} from '../api/stories.js';
import {
  isStoryMediaBusy,
  isStoryMediaProcessing,
  mergeLocalMediaItems,
  storyMediaStatusLabel,
  storyVideoMimeForFile,
  waitForStoryMediaToSettle,
} from '../lib/story-media-upload.js';

interface VideoItem {
  localId: string;
  mediaId?: string;
  file?: File;
  previewUrl?: string;
  status: string;
  progress: number;
  error?: string;
}

const CONCURRENCY = 2;

function fromServer(item: PublicStoryMedia, local?: VideoItem): VideoItem {
  return {
    localId: local?.localId ?? item.mediaId,
    mediaId: item.mediaId,
    file: local?.file,
    previewUrl: local?.previewUrl,
    status: item.status,
    progress: item.status === 'READY' || item.status === 'APPROVED' ? 100 : local?.progress ?? 0,
    error: local?.error,
  };
}

function isCellular(): boolean {
  const connection = (navigator as Navigator & { connection?: { type?: string; saveData?: boolean } })
    .connection;
  return connection?.type === 'cellular' || Boolean(connection?.saveData);
}

export function StoryVideoPicker({
  token,
  onBusyChange,
  onCountChange,
}: {
  token: string;
  onBusyChange: (busy: boolean) => void;
  onCountChange?: (count: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const previewUrls = useRef(new Set<string>());
  const started = useRef(new Set<string>());
  const [items, setItems] = useState<VideoItem[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [queue, setQueue] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      for (const url of previewUrls.current) {
        URL.revokeObjectURL(url);
      }
      previewUrls.current.clear();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listPublicStoryMedia(token)
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        setItems((current) => {
          const byMediaId = new Map(
            current.filter((item) => item.mediaId).map((item) => [item.mediaId, item]),
          );
          const listed = result.items
            .filter((item) => item.mediaType === 'VIDEO')
            .map((item) => fromServer(item, byMediaId.get(item.mediaId)));
          return mergeLocalMediaItems(current, listed);
        });
        for (const item of result.items.filter((entry) => entry.mediaType === 'VIDEO')) {
          if (!isStoryMediaProcessing(item.status)) {
            continue;
          }
          const settled = await waitForStoryMediaToSettle(token, item.mediaId, item);
          if (cancelled) {
            return;
          }
          setItems((current) =>
            current.map((entry) =>
              entry.mediaId === settled.mediaId ? fromServer(settled, entry) : entry,
            ),
          );
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    onBusyChange(items.some((item) => isStoryMediaBusy(item.status)));
    onCountChange?.(items.length);
  }, [items, onBusyChange, onCountChange]);

  useEffect(() => {
    const pending = queue.filter((id) => items.find((item) => item.localId === id)?.file);
    const active = items.filter((item) => item.status === 'UPLOADING' && item.mediaId).length;
    if (!pending.length || active >= CONCURRENCY) {
      return;
    }
    const nextId = pending[0];
    if (!nextId || started.current.has(nextId)) {
      return;
    }
    const next = items.find((item) => item.localId === nextId);
    if (!next?.file) {
      return;
    }
    started.current.add(nextId);
    setQueue((current) => current.filter((id) => id !== nextId));
    void uploadOne(next.file, next.localId).finally(() => {
      started.current.delete(nextId);
    });
  }, [queue, items]);

  const remaining = DEFAULT_STORY_MAX_VIDEOS - items.length;

  function rememberPreview(file: File): string {
    const url = URL.createObjectURL(file);
    previewUrls.current.add(url);
    return url;
  }

  function forgetPreview(url: string | undefined): void {
    if (!url) {
      return;
    }
    URL.revokeObjectURL(url);
    previewUrls.current.delete(url);
  }

  async function uploadOne(file: File, localId: string): Promise<void> {
    const mime = storyVideoMimeForFile(file);
    if (!mime) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, status: 'REJECTED_AUTOMATED', error: 'Use an MP4, MOV, or WebM video.' }
            : item,
        ),
      );
      return;
    }
    if (file.size > DEFAULT_STORY_VIDEO_MAX_BYTES) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, status: 'REJECTED_AUTOMATED', error: 'That video is larger than the 100 MB limit.' }
            : item,
        ),
      );
      return;
    }
    try {
      const grant = await initiateStoryPhoto(
        token,
        { mediaType: 'VIDEO', declaredMimeType: mime, sizeBytes: file.size },
        localId,
      );
      if (grant.uploadMode !== 'MULTIPART') {
        throw new Error('VIDEO_UPLOAD_FAILED');
      }
      setItems((current) =>
        current.map((item) =>
          item.localId === localId ? { ...item, mediaId: grant.mediaId, status: 'UPLOADING' } : item,
        ),
      );
      const partCount = Math.ceil(file.size / grant.partSizeBytes);
      const completed: Array<{ partNumber: number; eTag: string }> = [];
      for (let start = 1; start <= partCount; start += grant.maximumPartsPerRequest) {
        const batch = Array.from(
          { length: Math.min(grant.maximumPartsPerRequest, partCount - start + 1) },
          (_, index) => start + index,
        );
        const signed = await signStoryMediaParts(token, grant.mediaId, { partNumbers: batch });
        for (const part of signed.parts) {
          const offset = (part.partNumber - 1) * grant.partSizeBytes;
          const blob = file.slice(offset, offset + grant.partSizeBytes);
          let eTag: string | undefined;
          for (let attempt = 0; attempt < 3 && !eTag; attempt += 1) {
            try {
              eTag = await uploadStoryPart(part.uploadUrl, blob, part.headers, (percent) => {
                const overall = Math.round(((part.partNumber - 1 + percent / 100) / partCount) * 100);
                setItems((current) =>
                  current.map((item) =>
                    item.localId === localId ? { ...item, progress: overall } : item,
                  ),
                );
              });
            } catch {
              if (attempt === 2) {
                throw new Error('VIDEO_PART_FAILED');
              }
            }
          }
          if (!eTag) {
            throw new Error('VIDEO_PART_FAILED');
          }
          completed.push({ partNumber: part.partNumber, eTag });
        }
      }
      const finished = await completeStoryPhoto(token, grant.mediaId, { parts: completed });
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, mediaId: finished.mediaId, status: finished.status, progress: 100, error: undefined }
            : item,
        ),
      );
      const settled = await waitForStoryMediaToSettle(token, grant.mediaId, finished);
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                mediaId: settled.mediaId,
                status: settled.status,
                progress: 100,
                error:
                  settled.status === 'FAILED_RETRYABLE'
                    ? 'This video is still being checked. Please retry in a moment.'
                    : undefined,
              }
            : item,
        ),
      );
    } catch (caught) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                status: 'FAILED_RETRYABLE',
                error: caught instanceof ApiError ? caught.message : 'This video could not be uploaded.',
              }
            : item,
        ),
      );
    }
  }

  function onPick(files: FileList | null): void {
    if (!files?.length) {
      return;
    }
    setError(undefined);
    const nextFiles = Array.from(files).slice(0, Math.max(remaining, 0));
    if (files.length > nextFiles.length) {
      setError('You can add up to 5 videos.');
    }
    if (isCellular()) {
      setError('Short clips under 30 seconds upload fastest on a cellular connection.');
    }
    const additions = nextFiles.map((file) => ({
      localId: crypto.randomUUID(),
      file,
      previewUrl: rememberPreview(file),
      status: 'UPLOADING',
      progress: 0,
    }));
    setItems((current) => [...current, ...additions]);
    setQueue((current) => [...current, ...additions.map((item) => item.localId)]);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (cameraRef.current) {
      cameraRef.current.value = '';
    }
  }

  async function onRemove(item: VideoItem): Promise<void> {
    if (item.mediaId) {
      try {
        await removeStoryPhoto(token, item.mediaId);
      } catch {
        setError('The video could not be removed.');
        return;
      }
    }
    forgetPreview(item.previewUrl);
    started.current.delete(item.localId);
    setQueue((current) => current.filter((id) => id !== item.localId));
    setItems((current) => current.filter((entry) => entry.localId !== item.localId));
  }

  async function onRetry(item: VideoItem): Promise<void> {
    if (!item.file) {
      return;
    }
    setItems((current) =>
      current.map((entry) =>
        entry.localId === item.localId
          ? { ...entry, status: 'UPLOADING', progress: 0, error: undefined }
          : entry,
      ),
    );
    await uploadOne(item.file, item.localId);
  }

  return (
    <div className="mt-6 space-y-3">
      <input
        ref={inputRef}
        id="story-videos"
        className="sr-only"
        type="file"
        accept="video/*"
        multiple
        disabled={remaining <= 0}
        onChange={(event) => onPick(event.target.files)}
      />
      <input
        ref={cameraRef}
        id="story-videos-camera"
        className="sr-only"
        type="file"
        accept="video/*"
        capture="environment"
        disabled={remaining <= 0}
        onChange={(event) => onPick(event.target.files)}
      />
      <div className="flex flex-wrap gap-2">
        <button
          className="btn-secondary"
          type="button"
          disabled={remaining <= 0}
          onClick={() => inputRef.current?.click()}
        >
          {items.length ? 'Add another video' : 'Choose from library'}
        </button>
        <button
          className="btn-secondary"
          type="button"
          disabled={remaining <= 0}
          onClick={() => cameraRef.current?.click()}
        >
          Use camera
        </button>
      </div>
      <p className="text-sm text-charcoal/70">
        Up to 5 videos, 100 MB and 60 seconds each. Short clips under 30 seconds upload fastest.
      </p>
      {error ? (
        <p className="text-sm font-semibold text-crimson" role="alert">
          {error}
        </p>
      ) : null}
      {items.length ? (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={item.localId} className="rounded-lg border border-kraft/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-charcoal">Video {index + 1}</p>
                <p className="text-xs uppercase tracking-wide text-charcoal/70">
                  {storyMediaStatusLabel(item.status)}
                </p>
              </div>
              {item.previewUrl ? (
                <video
                  className="mt-2 max-h-48 w-full rounded bg-charcoal"
                  src={item.previewUrl}
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={`Video ${index + 1} preview`}
                />
              ) : null}
              {item.status === 'UPLOADING' || isStoryMediaProcessing(item.status) ? (
                <progress className="mt-2 w-full" max={100} value={item.status === 'UPLOADING' ? item.progress : 100} />
              ) : null}
              {item.error ? (
                <p className="mt-2 text-sm text-crimson" role="alert">
                  {item.error}
                </p>
              ) : null}
              <div className="mt-2 flex gap-2">
                {item.status === 'FAILED_RETRYABLE' && item.file ? (
                  <button className="btn-secondary" type="button" onClick={() => void onRetry(item)}>
                    Retry
                  </button>
                ) : null}
                <button className="btn-secondary" type="button" onClick={() => void onRemove(item)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
